import React, { useMemo } from 'react'
import useAppSelector from 'src/hooks/useAppSelector'

export type UseCustomisation = {
  previewMode: boolean
  sortingSectionId: string
  pageSections: KeyedSection[]
  pagePopups: KeyedSection[]
  pageOptions: Record<string, unknown>
  getLastSectionFromType: (type: string) => Customisation.Section
  getSectionFromId: (sectionId: string) => Customisation.Section
}

export interface KeyedSection extends Customisation.Section {
  key: string
}

type PagePath =
  | 'index'
  | 'product'
  | 'promotions'
  | 'stores'
  | 'checkout'
  | 'user'
  | 'thankyou'
  | 'terms'
  | 'privacy'
  | 'invoice'
  | 'loyalty'
  | 'orders'
  | 'reservations'

/**
 * A rewritten & simpler version of react hook
 * instead of passing props as container
 *
 * @borrows /fesc/containers/CustomisableApp.js
 */
const useCustomisation = (pagePath?: PagePath): UseCustomisation => {
  const { previewMode, sectionSettingData, sortingSectionId } = useAppSelector(
    (state) => state['@customisation'].data
  ) || {}

  const page = sectionSettingData.pages[pagePath]

  const pageSections = useMemo(
    () =>
      (page?.sections || []).map((sectionId) => ({
        ...sectionSettingData.sections[sectionId],
        key: sectionId
      })),
    [page?.sections, sectionSettingData.sections]
  )

  const pagePopups = useMemo(
    () =>
      (page?.popups || []).map((section) => ({
        ...sectionSettingData.sections[section],
        key: section
      })),
    [page?.popups, sectionSettingData.sections]
  )

  const getLastSectionFromType = React.useCallback(
    (type) => {
      const { sections } = sectionSettingData
      const sectionKey = Object.keys(sections)
        .filter((key) => (sections[key] || {}).type === type)
        .pop() // Get only the last section
      return sections[sectionKey] || ({} as Customisation.Section)
    },
    [sectionSettingData]
  )

  const getSectionFromId = React.useCallback(
    (sectionId) => sectionSettingData?.sections?.[sectionId] || ({} as Customisation.Section),
    [sectionSettingData]
  )

  return {
    previewMode,
    sortingSectionId,
    pageSections,
    pagePopups,
    pageOptions: (page || {}).options || {},
    getLastSectionFromType,
    getSectionFromId
  }
}

export default useCustomisation
